import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import GeospatialQueries, { GEOSPATIAL_QUERY_KEYS, isGeometryResponse } from '../query';
import { useModal } from '@stores';
import statusModal from '@forms/components/modals/statusModal';
import { toast } from 'react-toastify';
import { ValidationErrorModal } from '@forms/components/modals/ValidationModal';
import { ResponseStatuses } from '@src/utils/enums';
import { ValidationError } from '../types';

type GeometryUploadResult =
  | { success: true; data: unknown }
  | {
      success: false;
      reason: 'timeout' | 'gateway' | 'processing' | 'emptyGeometry' | 'validation';
      message?: string;
    };

export const useGetGeometryDataMutation = () => {
  const queryClient = useQueryClient();
  const { openSecondaryModal } = useModal();
  const allValidationErrorsRef = useRef<{ fileName: string; message: string; details: ValidationError[] }[]>([]);
  const uploadCountRef = useRef(0);

  const processValidationResponse = ({
    data,
    gdbFile,
    msg,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    gdbFile: File;
    msg?: string;
  }): GeometryUploadResult => {
    const fallbackMessage = 'GDB Upload encountered validation errors.';

    if (Array.isArray(data?.data)) {
      allValidationErrorsRef.current.push({
        fileName: gdbFile.name,
        message: msg ?? fallbackMessage,
        details: data.data.map((detail: { message: string; msg: string }) => ({
          ...detail,
          message: detail.message ?? detail.msg,
          fileName: gdbFile.name,
        })),
      });
    } else {
      openSecondaryModal(statusModal, {
        msg: msg ?? fallbackMessage,
        status: data?.status ?? ResponseStatuses.ValidationFailed,
      });
    }

    return {
      success: false,
      reason: 'validation',
      message: msg ?? fallbackMessage,
    };
  };

  return useMutation({
    mutationFn: async ({ gdbFile, firstPolygonOnly }: { gdbFile: File; firstPolygonOnly: boolean }) => {
      uploadCountRef.current += 1;
      const toastId = toast.loading('Uploading GDB file...');
      try {
        const data = await GeospatialQueries.getGeometryData(gdbFile, firstPolygonOnly);

        const featureCount = isGeometryResponse(data) ? (data?.data?.features?.length ?? 0) : null;

        const msg = data?.msg;
        const hasValidationError = data?.status === ResponseStatuses.ValidationFailed;

        toast.dismiss(toastId);

        if (hasValidationError) {
          return processValidationResponse({
            data,
            gdbFile,
            msg,
          });
        }

        if (data?.status === ResponseStatuses.Failed) {
          return {
            success: false,
            reason: 'processing',
            message: data?.msg,
          } as GeometryUploadResult;
        }

        if (featureCount === 0) {
          return {
            success: false,
            reason: 'emptyGeometry',
            message: 'The provided GDB file does not contain a valid geometry.',
          } as GeometryUploadResult;
        }

        if (!isGeometryResponse(data)) {
          return {
            success: false,
            reason: 'processing',
            message: 'Unexpected response format from geometry upload.',
          } as GeometryUploadResult;
        }

        toast.success('GDB file uploaded successfully');

        return {
          success: true,
          data: data.data ?? [],
        } as GeometryUploadResult;
      } catch (error) {
        toast.dismiss(toastId);

        const response = (
          error as {
            response?: {
              status?: number;
              data?: {
                msg?: string;
              };
            };
          }
        )?.response;

        console.error('GDB upload failed:', error);
        if (response?.status === 413) {
          return {
            success: false,
            reason: 'validation',
            message: 'The compressed GDB archive exceeds the 5 MiB limit.',
          };
        }

        const reason =
          response?.status === 504
            ? 'timeout'
            : response?.status === 502 || response?.status === 503
              ? 'gateway'
              : 'processing';
        return {
          success: false,
          reason,
          message: response?.data?.msg,
        } as GeometryUploadResult;
      } finally {
        uploadCountRef.current -= 1;

        if (uploadCountRef?.current === 0 && allValidationErrorsRef?.current.length > 0) {
          const fileMessages = 'Some files encountered validation errors.';
          const fileDetails = allValidationErrorsRef.current.flatMap((f) => f.details ?? []);

          openSecondaryModal(statusModal, {
            msg: <ValidationErrorModal message={fileMessages} details={fileDetails} />,
            status: ResponseStatuses.ValidationFailed,
          });

          allValidationErrorsRef.current = [];
        }
      }
    },
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(
        [GEOSPATIAL_QUERY_KEYS.FIND_ADDRESS_CANDIDATES, variables.gdbFile, variables?.firstPolygonOnly],
        data
      );
    },
    onError: (error, variables) => {
      //@TODO handle error appropriately, e.g., show an error modal or toast
      queryClient.setQueryData(
        [GEOSPATIAL_QUERY_KEYS.FIND_ADDRESS_CANDIDATES, variables.gdbFile, variables?.firstPolygonOnly],
        undefined
      );
      console.error('get geometry data mutation failed:', error);
    },
  });
};
